import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { posLinkUnit } from "./PosItemLinkEditor";

const API = "/api/purchasing/pos-catalog/sales";
const PAGE_SIZE = 50;
const blankFilters = () => ({ q: "", date_from: "", date_to: "" });
const canRead = (user) => Number(user?.id) === 1 || user?.permissions?.["purchasing.items.view"] === true;
const numberValue = (value) => value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);
const COPY = {
  es: {
    title: "Ventas POS", search: "Buscar ticket, código o nombre", from: "Desde", to: "Hasta", apply: "Aplicar", clear: "Limpiar filtros", reload: "Recargar",
    invalidDates: "La fecha inicial no puede ser posterior a la final.", loading: "Cargando ventas del POS…", refreshing: "Actualizando ventas…",
    empty: "Todavía no se han recibido ventas del POS.", noResults: "No hay ventas que coincidan con estos filtros.",
    failed: "No se han podido cargar las ventas del POS. Vuelve a intentarlo.", failedRefresh: "No se han podido actualizar las ventas. Se muestran los últimos datos recibidos.",
    denied: "No tienes permiso para consultar las ventas del POS.", noUnit: "Selecciona una unidad de negocio para consultar las ventas del POS.",
    date: "Fecha", ticket: "Ticket", source: "POS de origen", state: "Estado POS", code: "Código", name: "Artículo POS", quantity: "Cantidad vendida",
    gross: "Precio unit. con IVA", net: "Precio unit. sin IVA", reported: "Importe informado", link: "Vinculación actual en Compras", discounted: "Artículos descontados",
    item: "Artículo", recipe: "Receta", unlinked: "Sin vincular", voided: "Anulada", noCurrency: "Moneda no indicada", unit: "Unitario", line: "Total de línea", unknown: "Base no indicada",
    fiscalNote: "—: dato no facilitado por el POS. Los precios con y sin IVA son unitarios e históricos.",
    stockNote: "El descuento se aplica a los artículos inventariables vinculados, desde la fecha configurada (incluida).",
    inventory: "Estado de inventario", inventoryPending: "Pendiente", inventoryApplied: "Aplicado", inventoryError: "Error a revisar",
    inventoryUnlinked: "Sin artículos inventariables", inventoryExcluded: "Sin descuento", inventoryRestricted: "Oculto por permisos",
    priorEffects: "Descuento de una revisión anterior", priorRevision: "Revisión aplicada", unitMissing: "Unidad no indicada",
    issues: "Incidencias pendientes de inventario (todas las ventas recibidas)", issueGeneric: "Incidencia de inventario pendiente de revisión.", issuesShown: "Incidencias mostradas",
    previous: "Anterior", next: "Siguiente", page: "Página", of: "de", lines: "líneas de venta", pagination: "Páginas de ventas POS",
  },
  en: {
    title: "POS sales", search: "Search ticket, code or name", from: "From", to: "To", apply: "Apply", clear: "Clear filters", reload: "Reload",
    invalidDates: "The start date cannot be after the end date.", loading: "Loading POS sales…", refreshing: "Refreshing sales…",
    empty: "No POS sales have been received yet.", noResults: "No sales match these filters.",
    failed: "Could not load POS sales. Please try again.", failedRefresh: "Could not refresh sales. The last received data is displayed.",
    denied: "You do not have permission to view POS sales.", noUnit: "Select a business unit to view POS sales.",
    date: "Date", ticket: "Ticket", source: "Source POS", state: "POS status", code: "Code", name: "POS item", quantity: "Quantity sold",
    gross: "Unit price incl. VAT", net: "Unit price excl. VAT", reported: "Reported amount", link: "Current Purchasing link", discounted: "Deducted items",
    item: "Item", recipe: "Recipe", unlinked: "Not linked", voided: "Voided", noCurrency: "Currency not specified", unit: "Per unit", line: "Line total", unknown: "Basis not specified",
    fiscalNote: "—: data not provided by the POS. Prices including and excluding VAT are historical unit prices.",
    stockNote: "Inventory deductions apply to linked stock items from the configured start date, inclusive.",
    inventory: "Inventory status", inventoryPending: "Pending", inventoryApplied: "Applied", inventoryError: "Error to review",
    inventoryUnlinked: "No stock items", inventoryExcluded: "No deduction", inventoryRestricted: "Hidden by permissions",
    priorEffects: "Deduction from a previous revision", priorRevision: "Applied revision", unitMissing: "Unit not specified",
    issues: "Pending inventory issues (all received sales)", issueGeneric: "Inventory issue awaiting review.", issuesShown: "Issues shown",
    previous: "Previous", next: "Next", page: "Page", of: "of", lines: "sales lines", pagination: "POS sales pages",
  },
};

export default function PosSalesTab() {
  const { languageId } = usePmsTheme();
  const copy = COPY[languageId] || COPY.en;
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const [user, setUser] = useState(readStoredUser);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [draft, setDraft] = useState(blankFilters);
  const [filters, setFilters] = useState(blankFilters);
  const [page, setPage] = useState(1);
  const [filterError, setFilterError] = useState(false);
  const [data, setData] = useState({ key: "", rows: [], count: 0 });
  const [issues, setIssues] = useState({ scope: "", rows: [], count: 0 });
  const [requestState, setRequestState] = useState({ key: "", loading: true, failed: false });
  const generation = useRef(0);
  const pending = useRef(null);
  const permission = canRead(user);
  const unitId = user?.active_business_unit_id;
  const scope = `${user?.id}:${unitId}:${permission}:${sessionVersion}`;
  const key = JSON.stringify([scope, filters, page]);
  const keyRef = useRef(key);
  keyRef.current = key;
  const ready = data.key === key;
  const loading = requestState.key !== key || requestState.loading;
  const failed = requestState.key === key && requestState.failed;

  useEffect(() => {
    const update = (event) => {
      if (event.type === "storage" && event.key && !["palantir_user", "palantir_token"].includes(event.key)) return;
      generation.current += 1; pending.current = null;
      setUser(readStoredUser()); setSessionVersion((current) => current + 1);
      setDraft(blankFilters()); setFilters(blankFilters()); setPage(1); setFilterError(false);
    };
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const load = useCallback(async () => {
    if (!permission || !unitId || pending.current?.key === key) return;
    const sameSession = () => {
      const current = readStoredUser();
      if (String(current?.id) === String(user?.id) && String(current?.active_business_unit_id) === String(unitId) && canRead(current)) return true;
      setUser(current); setSessionVersion((version) => version + 1); setPage(1);
      return false;
    };
    if (!sameSession()) return;
    const version = ++generation.current;
    pending.current = { key, version };
    setRequestState({ key, loading: true, failed: false });
    try {
      const params = { page, page_size: PAGE_SIZE };
      Object.entries(filters).forEach(([name, value]) => { if (value) params[name] = value; });
      const response = await axios.get(API, { params, pilcoreExpectedUserId: user?.id, headers: { "X-Pilcore-Business-Unit-Id": String(unitId) } });
      if (version !== generation.current || keyRef.current !== key || !sameSession()) return;
      const count = numberValue(response.data?.count);
      if (!Array.isArray(response.data?.rows) || count === null || count < 0 || !Number.isInteger(count)
        || (response.data.page != null && Number(response.data.page) !== page)
        || (response.data.page_size != null && Number(response.data.page_size) !== PAGE_SIZE)) throw new Error("Invalid sales response");
      const issueRows = response.data.inventory_issues ?? [];
      const issueCount = numberValue(response.data.inventory_issue_count ?? issueRows.length);
      if (!Array.isArray(issueRows) || issueCount === null || !Number.isInteger(issueCount) || issueCount < 0) throw new Error("Invalid inventory issues response");
      setIssues({ scope, rows: issueRows.filter((issue) => issue && typeof issue === "object").slice(0, 25), count: issueCount });
      const lastPage = Math.max(1, Math.ceil(count / PAGE_SIZE));
      if (page > lastPage) { setPage(lastPage); return; }
      setData({ key, rows: response.data.rows, count });
      setRequestState({ key, loading: false, failed: false });
    } catch (_error) {
      if (version === generation.current && keyRef.current === key) setRequestState({ key, loading: false, failed: true });
    } finally {
      if (pending.current?.version === version) pending.current = null;
    }
  }, [filters, key, page, permission, scope, unitId, user?.id]);

  useEffect(() => {
    if (!permission || !unitId) return undefined;
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      window.clearInterval(timer); generation.current += 1;
      if (pending.current?.key === key) pending.current = null;
    };
  }, [load, permission, unitId, key]);

  const applyFilters = (event) => {
    event.preventDefault();
    if (draft.date_from && draft.date_to && draft.date_from > draft.date_to) { setFilterError(true); return; }
    setFilterError(false); setPage(1);
    setFilters({ ...draft, q: draft.q.trim() });
  };
  const clearFilters = () => { setDraft(blankFilters()); setFilters(blankFilters()); setPage(1); setFilterError(false); };
  const formatNumber = (value, money = false) => {
    const number = numberValue(value);
    return number === null ? "—" : number.toLocaleString(locale, { minimumFractionDigits: money ? 2 : 0, maximumFractionDigits: 6 });
  };
  const formatMoney = (value, currency) => numberValue(value) === null ? "—" : `${formatNumber(value, true)}${currency ? ` ${currency}` : ""}`;
  const formatDate = (value) => {
    if (!value) return "—";
    const parts = String(value).slice(0, 10).split("-").map(Number);
    if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return "—";
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    if (date.getFullYear() !== parts[0] || date.getMonth() !== parts[1] - 1 || date.getDate() !== parts[2]) return "—";
    return date.toLocaleDateString(locale, { dateStyle: "medium" });
  };
  const hasFilters = Object.values(filters).some(Boolean);
  const rows = ready ? data.rows : [];
  const pages = ready ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const inventoryView = (row) => {
    if (row.inventory_restricted) return { status: copy.inventoryRestricted, content: copy.inventoryRestricted };
    const effects = Array.isArray(row.inventory_effects) ? row.inventory_effects : [];
    const appliedRevision = numberValue(row.inventory_applied_revision);
    const saleRevision = numberValue(row.sale_revision);
    const olderRevision = appliedRevision !== null && saleRevision !== null && appliedRevision !== saleRevision;
    const state = row.inventory_status === "applied" && olderRevision ? "pending" : row.inventory_status;
    const status = { pending: copy.inventoryPending, applied: copy.inventoryApplied, error: copy.inventoryError,
      unlinked: copy.inventoryUnlinked, excluded: copy.inventoryExcluded }[state] || "—";
    const previous = effects.length > 0 && (olderRevision || state === "pending" || state === "error");
    return {
      status, error: state === "error" && typeof row.inventory_error === "string" ? row.inventory_error : null,
      content: effects.length ? <>
        {previous && <p className="posSales-priorEffects">{copy.priorEffects}{appliedRevision !== null && <small>{copy.priorRevision}: {appliedRevision}</small>}</p>}
        <ul className="posSales-effects">{effects.map((effect, index) => <li key={effect.movement_id || `${effect.line_id}:${effect.item_id}:${index}`}>
          <div><strong>{effect.name || effect.code || "—"}</strong>{effect.code && <small>{effect.code}</small>}</div>
          <span>{formatNumber(effect.quantity)} {posLinkUnit(effect.unit, copy.unitMissing)}</span>
        </li>)}</ul>
      </> : state === "pending" || state === "error" ? "—" : numberValue(row.discounted_quantity) === 0 ? "0" : "—",
    };
  };

  if (!permission) return <p className="spx-empty" role="alert">{copy.denied}</p>;
  if (!unitId) return <p className="spx-empty">{copy.noUnit}</p>;
  return <section className="posSales" aria-label={copy.title} aria-busy={loading}>
    <form className="spx-toolbar posSales-filters" onSubmit={applyFilters}>
      <label className="posSales-search">{copy.search}<input className="spx-search" type="search" maxLength={120} value={draft.q} onChange={(event) => setDraft((current) => ({ ...current, q: event.target.value }))} /></label>
      <label>{copy.from}<input className="spx-input" type="date" value={draft.date_from} onChange={(event) => setDraft((current) => ({ ...current, date_from: event.target.value }))} /></label>
      <label>{copy.to}<input className="spx-input" type="date" value={draft.date_to} onChange={(event) => setDraft((current) => ({ ...current, date_to: event.target.value }))} /></label>
      <div className="posSales-filterActions"><button type="submit" className="spx-btn">{copy.apply}</button>
        <button type="button" className="spx-btn" onClick={clearFilters} disabled={!Object.values(draft).some(Boolean) && !hasFilters}>{copy.clear}</button>
        <button type="button" className="spx-btn" onClick={load} disabled={loading}><FiRefreshCw aria-hidden="true" />{copy.reload}</button></div>
    </form>
    {filterError && <p className="spx-banner spx-banner--error" role="alert">{copy.invalidDates}</p>}
    <div className="posSales-notes"><p>{copy.fiscalNote}</p><p>{copy.stockNote}</p></div>
    {failed && <p className="spx-banner spx-banner--error" role="alert">{ready ? copy.failedRefresh : copy.failed}</p>}
    {issues.scope === scope && issues.count > 0 && <aside className="spx-banner spx-banner--error posSales-issues" role="alert" aria-label={copy.issues}>
      <strong>{copy.issues}: {issues.count}</strong>
      <ul>{issues.rows.map((issue, index) => <li key={issue.id ?? index}>
        <strong>{copy.ticket} {issue.ticket_number || "—"}</strong> · {issue.source_name || "—"} · {formatDate(issue.business_date)}
        <span>{typeof issue.inventory_error === "string" && issue.inventory_error ? issue.inventory_error : copy.issueGeneric}</span>
      </li>)}</ul>
      {issues.count > issues.rows.length && <p>{copy.issuesShown}: {issues.rows.length} {copy.of} {issues.count}</p>}
    </aside>}
    {!ready ? loading ? <p className="spx-empty" role="status">{copy.loading}</p> : null : !rows.length ? <p className="spx-empty" role="status">{hasFilters ? copy.noResults : copy.empty}</p> :
      <div className="spx-tableWrap" tabIndex={0} role="region" aria-label={copy.title}>
        <table className="spx-table posSales-table" aria-label={copy.title}>
          <thead><tr>{[copy.date, copy.ticket, copy.source, copy.state, copy.code, copy.name, copy.quantity, copy.gross, copy.net, copy.reported, copy.link, copy.inventory, copy.discounted].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{rows.map((row) => { const inventory = inventoryView(row); return <tr key={row.id} className={row.is_voided ? "posSales-voided" : undefined}>
            <td>{formatDate(row.business_date)}</td><td>{row.ticket_number || "—"}</td><td>{row.source_name || "—"}</td>
            <td>{row.is_voided ? <strong>{copy.voided}</strong> : row.source_status || "—"}{row.is_voided && row.source_status && <small>{row.source_status}</small>}</td>
            <td className="spx-code">{row.code || "—"}</td><td>{row.name || "—"}</td><td className="posSales-number">{formatNumber(row.quantity)}</td>
            <td className="posSales-number">{formatMoney(row.unit_price_incl_tax, row.currency)}{numberValue(row.unit_price_incl_tax) !== null && !row.currency && <small>{copy.noCurrency}</small>}</td>
            <td className="posSales-number">{formatMoney(row.unit_price_excl_tax, row.currency)}{numberValue(row.unit_price_excl_tax) !== null && !row.currency && <small>{copy.noCurrency}</small>}</td>
            <td className="posSales-number">{formatMoney(row.reported_amount, row.currency)}{numberValue(row.reported_amount) !== null && <><small>{["unit", "line"].includes(row.reported_amount_basis) ? copy[row.reported_amount_basis] : copy.unknown}</small>{!row.currency && <small>{copy.noCurrency}</small>}</>}</td>
            <td className="posCatalog-link">{row.link ? <><strong>{copy[row.link.target_type]} · {row.link.target_name || row.link.target_code || "—"}</strong><small>{row.link.target_code || ""}</small></> : copy.unlinked}</td>
            <td className="posSales-inventoryStatus"><strong>{inventory.status}</strong>{inventory.error && <small className="posSales-inventoryError">{inventory.error}</small>}</td>
            <td className="posSales-deductions">{inventory.content}</td>
          </tr>; })}</tbody>
        </table>
      </div>}
    <footer className="posSales-pagination" aria-label={copy.pagination}>
      <span aria-live="polite">{ready ? `${data.count} ${copy.lines} · ${copy.page} ${page} ${copy.of} ${pages}` : "—"}{loading && ready ? ` · ${copy.refreshing}` : ""}</span>
      <div><button type="button" className="spx-btn" disabled={loading || !ready || page <= 1} onClick={() => setPage((current) => current - 1)}>{copy.previous}</button>
        <button type="button" className="spx-btn" disabled={loading || !ready || page >= pages} onClick={() => setPage((current) => current + 1)}>{copy.next}</button></div>
    </footer>
  </section>;
}
